import axios from '@/plugins/axios'
import { Module } from 'vuex'

const networkConnectionsModule: Module<any, any> = {
	state: {
		connectedOrManagedNetworkConnections: null,
		selectedNetworkConnection: null,
		persistedSelectedNetworkConnectionId: null, // We save this in local storage and reference it when loading network connections so we can use the last-selected network connection
		selectedNetworkConnectionsByNetwork: null,
	},
    actions: {
        async getNetworkConnections(_, params) {

            const page = params?.page ? '&page=' + params.page : ''
            
            const perPage = params?.perPage
                ? '&itemPerPage=' + params.perPage
                : ''
                
            const workspaceMemberId = params?.workspaceMemberId
                ? '&workspaceMemberId=' + params.workspaceMemberId
                : ''
                
            const workspaceMemberIds = params?.workspaceMemberIds
                ? '&workspaceMemberIds=' + params.workspaceMemberIds
                : ''
                
            const connectedTo = params?.connectedTo
                ? '&connectedTo=' + params.connectedTo
                : ''

            const connectedOrManagedBy = params?.connectedOrManagedBy
                ? '&connectedOrManagedBy=' + params.connectedOrManagedBy
                : ''

            const active = params?.active
                ? '&active=1'
                : ''

            const connectionType = params?.connectionType
                ? '&connectionType=' + params.connectionType
                : ''

            const publishing = params?.publishing
                ? '&publishing=' + params.publishing
                : ''
            
            const from = params?.from 
                ? '&reachAt[from]=' + params.from 
                :''

            const to = params?.to 
                ? '&reachAt[to]=' + params.to
                :''  
            
            const network = params?.network 
                ? '&network=' + params.network
                : ''
            
            const networks = params?.networks
                ? '&networks=' + params.networks.join(',')
                : ''

            const vanityName = params?.vanityName 
                ? '&vanityName=' +  params.vanityName
                : ''

            let networkConnectionIds  = ''
            
            if (params?.networkConnectionIds) {
				if (Array.isArray(params.networkConnectionIds)) {
					networkConnectionIds = '&' + params.networkConnectionIds.map(id => 'networkConnectionIds[]=' + id).join('&')
				} else {
					networkConnectionIds = '&networkConnectionIds=' + params.networkConnectionIds
				}
            }

            const parentNetworkUserId = params?.parentNetworkUserId 
                ? '&parentNetworkUserId=' +  params.parentNetworkUserId
                : ''      

            const managedBy = params?.managedBy
                ? '&managedBy=' + params.managedBy
                : ''                

            const reportId = params?.reportId
                ? '&reportId=' + params.reportId
                : ''

            const sortOrder = params?.sortOrder
                ? '&order=' + params.sortOrder
                : ''

            const sortKey = params?.sortKey
                ? '&sort=' + params.sortKey
                : ''    
            
            const url =
                '/api/network_connections?' +
                page +
                perPage +
                workspaceMemberId + 
                workspaceMemberIds + 
                active + 
                connectionType + 
                connectedTo + 
                publishing +
                from + 
                to + 
                network +
                networks +
                managedBy + 
                connectedOrManagedBy + 
                vanityName + 
                networkConnectionIds + 
                reportId +
                parentNetworkUserId + 
                sortOrder + 
                sortKey
                
            const { data } = await axios.get(url)
            return data
        },

        async exportNetworkConnections(_, params) {
            
            const page = params?.page ? '&page=' + params.page : ''
            
            const perPage = params?.perPage || params?.perPage===0 
                ? '&itemPerPage=' + params.perPage
                : ''
                
            const workspaceMemberId = params?.workspaceMemberId
                ? '&workspaceMemberId=' + params.workspaceMemberId
                : ''
                
            const connectedTo = params?.connectedTo
                ? '&connectedTo=' + params.connectedTo
                : ''

            const active = params?.active
                ? '&active=1'
                : ''

            const connectionType = params?.connectionType
                ? '&connectionType=' + params.connectionType
                : ''

            const publishing = params?.publishing
                ? '&publishing=' + params.publishing
                : ''
            
            const from = params?.from 
                ? '&reachAt[from]=' + params.from 
                :''

            const to = params?.to 
                ? '&reachAt[to]=' + params.to
                :''  
            
            const network = params?.network 
                ? '&network=' +  params.network
                : ''

            const vanityName = params?.vanityName 
                ? '&vanityName=' +  params.vanityName
                : ''

            const reportId = params?.reportId
                ? '&reportId=' +  params.reportId
                : ''

            const networkConnectionIds = params?.networkConnectionIds 
                ? '&networkConnectionIds=' +  params.networkConnectionIds
                : ''

            const sortOrder = params?.sortOrder
                ? '&order=' + params.sortOrder
                : ''

            const sortKey = params?.sortKey
                ? '&sort=' + params.sortKey
                : ''    

            const url =
                '/api/network_connections/export?' +
                page +
                perPage +
                workspaceMemberId + 
                active + 
                connectionType + 
                connectedTo + 
                publishing +
                from + 
                to + 
                network +
                vanityName + 
                networkConnectionIds + 
                reportId +
                sortOrder + 
                sortKey

            const { data } = await axios.get(url)
            return data
        },

        async getNetworkConnection(_, id) {
            const url = '/api/network_connections/' + id
            const { data } = await axios.get(url)
            return data
        },

        async requestNetworkConnectionRefresh(_, id) {
            const url = '/api/network_connections/requestrefresh/' + id
            const { data } = await axios.put(url, {})
            return data
        },

        async getFollowerInsights({state}, params) {
            const url = '/api/follower_insights?' + params.networkConnectionIds?.map(ncId => 'networkConnectionIds[]=' + ncId).join('&')
            const { data } = await axios.get(url)
            return data
        },

        async getPersistedNetworkConnections({state}, params) {
            const url = '/api/network_connections'
            const query = {};

            if(params?.connectedTo) {
                query['connectedTo'] = params.connectedTo
            }

            if(params?.workspaceMemberId) {
                query['workspaceMemberId'] = params.workspaceMemberId
            }

            const { data } = await axios.get(url, {
                params: {
                    ...query
                }
            })

            state.activeNetworkConnections = data
            return data
        },

        async getConnectedOrManagedNetworkConnections({state, getters}, workspaceMemberId) {
            
            const url = '/api/network_connections?active=true&publishing=true&connectedOrManagedBy=' + workspaceMemberId
            const { data } = await axios.get(url)
            
			state.connectedOrManagedNetworkConnections = data['hydra:member']
			state.selectedNetworkConnection = {}
			state.selectedNetworkConnectionsByNetwork = {}
			
			if (!state.selectedNetworkConnection && state.persistedSelectedNetworkConnectionId) {
				state.selectedNetworkConnection = data['hydra:member'].find(nc => nc.id == state.persistedSelectedNetworkConnectionId)
			}
			
			const networks = ['linkedin', 'twitter', 'facebook', 'instagram']
			networks.forEach(network => {
			
				let ncs = data['hydra:member'].filter(nc => nc.network.toLowerCase() == network)
			
				state.selectedNetworkConnectionsByNetwork[network] = 
					ncs.find(nc => nc.id == state.persistedSelectedNetworkConnectionId) ??
					ncs.find(nc => nc.workspaceMemberId == getters.authedMemberInfo.id && nc.accessTokenType == 'USER') ??
					ncs.find(nc => nc.workspaceMemberId == getters.authedMemberInfo.id) ??
					ncs[0] ??
					null
					
				if (!state.selectedNetworkConnection) {
					state.selectedNetworkConnection = state.selectedNetworkConnectionsByNetwork[network]
				}
				
			})

            return data
        },

        async disconnectNetworkConnection(_, id) {
            const url = '/api/network_connections/' + id
            const { data } = await axios.delete(url)
            return data
        },

        async deleteFollower(_, params) {
            const { networkConnectionId, profileUuid } = params
            const url = `/api/network_connections/followers/${networkConnectionId}`
            const { data } = await axios.delete(url, {
                data: {
                    confirmation: 'DELETE',
                    profileUuid: profileUuid,
                },
            })
            return data
        },
    },
    getters: {
        activeNetworkConnections: (state) => state.activeNetworkConnections,
        
        selectedNetworkConnection: (state, getters) => {
        	return state.selectedNetworkConnection ?? Object.values(state.selectedNetworkConnectionsByNetwork ?? {})[0] ?? null
        },

        selectedNetworkConnectionsByNetwork: (state, getters) => {
        	return state.selectedNetworkConnectionsByNetwork
        },
        
        connectedOrManagedNetworkConnections: (state, getters) => {
        	return state.connectedOrManagedNetworkConnections
        }
    },
    mutations: {
    	setSelectedNetworkConnection(state, networkConnection) {
    		state.selectedNetworkConnection = networkConnection
    		state.selectedNetworkConnectionsByNetwork[networkConnection.network.toLowerCase()] = networkConnection
    		state.persistedSelectedNetworkConnectionId = networkConnection.id
     	}
    }

}

export default networkConnectionsModule
